import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd"
import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { CollapsibleSection } from "components/collapsibleSection"
import { DropdownMenu } from "components/dropdownMenu";
import Input from "components/input";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { FieldObjectType, IAttendantTemplateData, IAttendantTemplateInputItem, InputType, SupportedLanguage, ValidationType } from "interfaces";
import { get } from "lodash"
import { Fragment, useEffect, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { InputTypeDropdown } from "../inputTypeDropdown";
import Select from "components/select";
import { DATE_INPUT_VALIDATIONS, FIELD_OBJECT_LIST, NUMBER_INPUT_VALIDATIONS, OPTIONS_BY_OBJECT, TEXT_INPUT_VALIDATIONS } from "utills/constants";
import { OptionForm } from "../optionForm";
import Button from "components/button";
import { MoveFieldModal } from "../moveFieldModal";
import { RightSideModal } from "components/rightSideModal";
import { IModalProps } from "components/modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FieldSchema } from "../validationSchemas";
import { languageSchema } from "routes/event/schema";
import DatePicker from "components/datePicker";
import moment from "moment";

interface IFields extends IAttendantTemplateInputItem {
  field_id: string;
}

interface IFieldFormProps extends IModalProps {
  templateIndex: number;
  groupIndex: number;
  groupFields: IAttendantTemplateData[];
  languages: SupportedLanguage[];
  isOpenRegistration: boolean;
  onSubmit: (data: IAttendantTemplateInputItem[]) => void;
  onMoveField: (movedField: IAttendantTemplateInputItem, updatedCurrentGroup: IAttendantTemplateInputItem[], currentGroupIndex: number, targetGroupIndex: number) => void;
}

const DEFAULT_NEW_FIELD = {
  label: {
    en: "",
    vi: "",
  },
  sub_label: {
    en: "",
    vi: "",
  },
  sub_label_url: "",
  is_lock_update_data: false,
  key: "",
  object: FieldObjectType.NONE,
  show: true,
  require: true,
  type: InputType.TEXT,
  validation: ValidationType.NONE,
  option: null,
};

const INITIAL_MOVE_FIELD_CONFIG = {
  open: false,
  index: -1
}

const FieldFormSchema = (languages: any) => {

  return z.object({
    fields: z.array(
      FieldSchema(languageSchema(languages), languages)
    )
  })
}

const DISABLED_KEY_FIELDS: string[] = ["first_name", "last_name", "phone", "email", "t_shirt_size_finisher"];
const DISABLED_SHOW_FIELDS: string[] = [];
const DISABLED_REQUIRE_FIELDS: string[] = [];
const DISABLED_LOCK_UPDATE_FIELDS: string[] = [];
const DISABLED_TYPE_FIELDS: string[] = ["first_name", "last_name", "phone", "email", "t_shirt_size_finisher"];
const DISABLED_LABEL_FIELDS: string[] = [];
const DISABLE_DELETE_KEY_FIELDS: string[] = ["first_name", "last_name", "phone", "email"];

export const FieldForm = ({templateIndex, groupIndex, groupFields, languages, open, onClose, onSubmit, onMoveField, isOpenRegistration }: IFieldFormProps) => {
  const [openFieldId, setOpenFieldId] = useState<number | null>(null);
  const [moveFieldModalConfig, setMoveFieldModalConfig] = useState(INITIAL_MOVE_FIELD_CONFIG);

  const methods = useForm<{fields: IAttendantTemplateInputItem[]}>({
    resolver: (values, context, options) => {
      const createResolver = zodResolver(FieldFormSchema(languages));
      return createResolver(values, context, options);
    },
  });

  const {
    control,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
    reset,
    trigger,
    register,
  } = methods;

  const { fields, move, append, remove } = useFieldArray({
    control,
    name: `fields`,
    keyName: "field_id"
  });

  useEffect(() => {
    if (open) {
      reset({
        fields: get(groupFields[groupIndex], "fields", []).map((field) => ({
          ...field,
          object: field.object ?? FieldObjectType.NONE
        }))
      });
      trigger("fields");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fieldsData = watch(`fields`);

  const handleDrag = ({ source, destination }: any) => {
    if (destination) {
      move(source.index, destination.index);
    }
  };

  const _handleOpenField = (index: number) => {
    setOpenFieldId(prevId => prevId === index ? null : index);
  }

  const _onCloseMoveFieldModal = () => {
    setMoveFieldModalConfig(INITIAL_MOVE_FIELD_CONFIG);
  }

  const onChangeValidation = (validationValue: ValidationType, fieldType: InputType, index: number) => {
    if([ValidationType.EMAIL, ValidationType.NONE, ValidationType.PHONE].indexOf(validationValue) > -1) {
      const updatedFields = [...watch("fields")];
      updatedFields[index].option = null;
      return reset({
        fields: updatedFields
      })
    }
    if([ValidationType.LENGTH, ValidationType.DOB].indexOf(validationValue) > -1) {
      const updatedFields = [...watch("fields")];
      const defaultMinValue = 
        validationValue === ValidationType.LENGTH 
        ? fieldType === InputType.TEXT ? 5 : 1
        : validationValue === ValidationType.DOB
          ? 12
          : 1;
      const defaultMaxValue = 
        validationValue === ValidationType.LENGTH 
        ? fieldType === InputType.TEXT ? 20 : 10
        : validationValue === ValidationType.DOB
          ? 105
          : 1;

      updatedFields[index].option = {
        min: defaultMinValue,
        max: defaultMaxValue,
        by: null
      };
      return reset({
        fields: updatedFields
      })
    }
    return;
  }

  const onChangeObject = (objectValue: any, index: number) => {
    const updatedFields = [...watch("fields")];
    updatedFields[index].option = objectValue !== "" ? get(OPTIONS_BY_OBJECT, `${objectValue}`, "") : null;
    return reset({
      fields: updatedFields
    })
  }

  const onChangeType = (index: number, value: string) => {
    const updatedFields = [...watch("fields")];
    updatedFields[index].option = null;
    updatedFields[index].object = value === InputType.SELECT ? FieldObjectType.CUSTOM : FieldObjectType.NONE;
    updatedFields[index].validation = ValidationType.NONE;

    reset({
      fields: updatedFields
    })
  }

  const getValidationList = (type: InputType) => {
    switch (type) {
      case InputType.TEXT: return TEXT_INPUT_VALIDATIONS;
      case InputType.DATE: return DATE_INPUT_VALIDATIONS;
      case InputType.NUMBER: return NUMBER_INPUT_VALIDATIONS;
      default: return [];
    }
  }

  const isShowOptionForm = (index: number) => {
    return (fieldsData[index].type === InputType.SELECT 
      && (fieldsData[index]?.object === FieldObjectType.CUSTOM || fieldsData[index]?.object === FieldObjectType.TSHIRT_SIZE_FINISHER || fieldsData[index]?.object === FieldObjectType.TSHIRT_SIZE)) 
        || fieldsData[index].type === InputType.RADIO_BUTTON 
        || fieldsData[index].type === InputType.MULTI_SELECT;
  }

  const _submit = (data: {fields: IAttendantTemplateInputItem[]}) => {
    onSubmit(data.fields);
  }

  console.log(errors);

  const systemFieldsIds = (groupFields[groupIndex]?.fields ?? []).map(systemField => fields.find(field => field.key === systemField.key) && (`${fields.find(field => field.key === systemField.key)?.field_id}`));
  const disabledKeyFieldsIds = DISABLED_KEY_FIELDS.map(systemFieldKey => fields.find(field => field.key === systemFieldKey)?.field_id).filter(item => item);
  const disabledShowFieldsIds = DISABLED_SHOW_FIELDS.map(systemFieldKey => fields.find(field => field.key === systemFieldKey)?.field_id).filter(item => item);
  const disabledRequireFieldsIds = DISABLED_REQUIRE_FIELDS.map(systemFieldKey => fields.find(field => field.key === systemFieldKey)?.field_id).filter(item => item);
  const disabledLockUpdateFieldsIds = DISABLED_LOCK_UPDATE_FIELDS.map(systemFieldKey => fields.find(field => field.key === systemFieldKey)?.field_id).filter(item => item);
  const disabledTypeFieldsIds = DISABLED_TYPE_FIELDS.map(systemFieldKey => fields.find(field => field.key === systemFieldKey)?.field_id).filter(item => item);
  const disabledLabelFieldsIds = DISABLED_LABEL_FIELDS.map(systemFieldKey => fields.find(field => field.key === systemFieldKey)?.field_id).filter(item => item);
  const disabledDeleteKeyFieldsIds = DISABLE_DELETE_KEY_FIELDS.map(systemFieldKey => fields.find(field => field.key === systemFieldKey)?.field_id).filter(item => item);

  return (
    <RightSideModal title="Add Field" closeOnClickBackdrop={false} open={open} onClose={onClose} footer={(
      <div className="w-full flex flex-row gap-4 items-center">
        <Button 
          className="w-full bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100" 
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button className="w-full uppercase" onClick={handleSubmit(_submit)}>Save</Button>
      </div>
    )}>
      <FormProvider {...methods}>
        <div className="px-4 py-4 sm:px-6">
          <div>
            <p className="text-sm font-medium">Group Info</p>
            <div className="mt-1 flex flex-row flex-wrap items-center gap-x-2">
              {languages.map((lang, langIndex) => (
                <Fragment key={lang}>
                  {langIndex !== 0 ? <span>•</span> : <></>}
                  <p className="text-sm" style={{wordBreak: "break-word"}}>
                    {get(groupFields[groupIndex], `group.label.${lang}`, "")}
                  </p>
                </Fragment>
              ))}
            </div>
          </div>
          <div className="mt-6 pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm font-medium">Fields setup</p>
          </div>
          <div className="mt-4 sm:mt-6">
            <DragDropContext onDragEnd={handleDrag}>
              <div className="grid sm:grid-cols-6 lg:gap-x-6">
                <Droppable droppableId="test-items">
                  {(provided, snapshot) => (
                    <div
                      className="col-span-full"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {fields.map((field: IFields, index: number) => (
                        <Draggable
                          key={`${field.field_id}${index}`}
                          draggableId={`item-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              className={`border rounded-lg mb-4 ${
                                get(errors, `fields.${index}`, "")
                                  ? "border-red-500"
                                  : openFieldId === index ? "border-brand-500" : "border-gray-300"
                              }`}
                              key={field.field_id}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <CollapsibleSection
                                label={`${get(
                                  watch("fields")[index], 
                                  `label.${languages.includes(SupportedLanguage.EN) ? "en" : languages[0] ?? 'vi'}`
                                )}`}
                                customOpen={{
                                  open: openFieldId === index,
                                  onClick: () => _handleOpenField(index)
                                }}
                                isAnimated={false}
                                toolbar={
                                  <DropdownMenu
                                    label={
                                      <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                                    }
                                    items={[
                                      ...(index !== 0
                                        ? [
                                            {
                                              label: "Move up",
                                              onClick: () =>
                                                move(index, index - 1),
                                            },
                                          ]
                                        : []),
                                      ...(index !== fields.length - 1
                                        ? [
                                            {
                                              label: "Move Down",
                                              onClick: () =>
                                                move(index, index + 1),
                                            },
                                          ]
                                        : []),
                                        {
                                          label: "Move Group Info",
                                          onClick: () => setMoveFieldModalConfig({
                                            open: true,
                                            index: index
                                          })
                                        },
                                        {
                                          label: "Duplicate",
                                          onClick: () =>
                                            append({
                                              ...field,
                                              key: ""
                                            }),
                                        },
                                        ...(((isOpenRegistration ? systemFieldsIds.includes(field.field_id) ? false: true : true) && !disabledDeleteKeyFieldsIds.includes(get(field, "field_id", "")))
                                          ? [
                                              {
                                                label: "Delete",
                                                onClick: () =>
                                                  remove(index),
                                              },
                                            ]
                                          : []),  
                                    ]}
                                  />
                                }
                                headerProps={{ ...provided.dragHandleProps }}
                              >
                                <div className="flex flex-col gap-y-4 px-4 py-4 sm:px-6">
                                  <div>
                                    <Label
                                      required
                                      id={`type_${index}`}
                                      name="Type"
                                    />
                                    <Controller
                                      name={`fields.${index}.type`}
                                      control={control}
                                      render={({ field: { onChange, value } }) => (
                                        <InputTypeDropdown 
                                          value={value}
                                          onChange={(value) => {
                                            onChange(value);
                                            onChangeType(index, value);
                                          }}
                                          disabled={disabledTypeFieldsIds.includes(get(field, "field_id", ""))}
                                        />
                                      )}
                                    />
                                    {get(
                                        errors,
                                        `fields.${index}.type`,
                                        ""
                                      ) ? (
                                      <p className="error-text">
                                        <span className="font-medium">
                                          {get(
                                            errors,
                                            `fields.${index}.type.message`,
                                            ""
                                          ) as string}
                                        </span>
                                      </p>
                                    ) : null}
                                  </div>
                                  <div>
                                    <Label
                                      required
                                      id={`label_${index}`}
                                      name="Field Name"
                                    />
                                    <div className="flex flex-col gap-y-4">
                                      {languages.map(
                                        (lang: SupportedLanguage) => (
                                          <div key={lang}>
                                            <div className="relative">
                                              <Input
                                                id={`label_${lang}_${index}`}
                                                type="text"
                                                placeholder="Field Name"
                                                className="pr-11"
                                                disabled={disabledLabelFieldsIds.includes(get(field, "field_id", ""))}
                                                error={get(
                                                  errors,
                                                  `fields.${index}.label.${lang}.message`,
                                                  ""
                                                )}
                                                {...register(`fields.${index}.label.${lang}`)}
                                              />
                                              <img
                                                src={`/images/flags/circle-${lang}.png`}
                                                alt="Language Icon"
                                                className="absolute top-1/2 right-3 -translate-y-1/2"
                                              />
                                            </div>
                                            {get(
                                                errors,
                                                `fields.${index}.label.${lang}`,
                                                ""
                                              ) ? (
                                              <p className="error-text">
                                                <span className="font-medium">
                                                  {get(
                                                    errors,
                                                    `fields.${index}.label.${lang}.message`,
                                                    ""
                                                  ) as string}
                                                </span>
                                              </p>
                                            ) : null}
                                          </div>
                                        )
                                      )}
                                    </div>

                                  </div>
                                  <div>
                                    <Label
                                      id={`sub_label_${index}`}
                                      name="Description"
                                    />
                                    <div className="flex flex-col gap-y-4">
                                      {languages.map(
                                        (lang: SupportedLanguage) => (
                                          <div key={lang}>
                                            <Controller
                                              name={`fields.${index}.sub_label.${lang}`}
                                              control={control}
                                              render={({ field }: any) => (
                                                <div className="relative">
                                                  <Input
                                                    id={`sub_label_${lang}_${index}`}
                                                    type="text"
                                                    placeholder="Description"
                                                    className="pr-11"
                                                    error={get(
                                                      errors,
                                                      `fields.${index}.sub_label.${lang}.message`,
                                                      ""
                                                    )}
                                                    {...field}
                                                  />
                                                  <img
                                                    src={`/images/flags/circle-${lang}.png`}
                                                    alt="Language Icon"
                                                    className="absolute top-1/2 right-3 -translate-y-1/2"
                                                  />
                                                </div>
                                              )}
                                            />
                                            {get(
                                                errors,
                                                `fields.${index}.sub_label.${lang}`,
                                                ""
                                              ) ? (
                                              <p className="error-text">
                                                <span className="font-medium">
                                                  {get(
                                                    errors,
                                                    `fields.${index}.sub_label.${lang}.message`,
                                                    ""
                                                  ) as string}
                                                </span>
                                              </p>
                                            ) : null}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <Label
                                      id={`sub_label_url`}
                                      name="Hyperlink for Description"
                                    />
                                    <Controller
                                      name={`fields.${index}.sub_label_url`}
                                      control={control}
                                      render={({ field }: any) => (
                                        <Input
                                          id={`sub_label_url`}
                                          type="text"
                                          placeholder="Hyperlink for Description"
                                          error={get(
                                            errors,
                                            `fields.${index}.sub_label_url.message`,
                                            ""
                                          )}
                                          {...field}
                                        />
                                      )}
                                    />
                                    {get(
                                        errors,
                                        `fields.${index}.sub_label_url`,
                                        ""
                                      ) ? (
                                      <p className="error-text">
                                        <span className="font-medium">
                                          {get(
                                            errors,
                                            `fields.${index}.sub_label_url.message`,
                                            ""
                                          ) as string}
                                        </span>
                                      </p>
                                    ) : null}
                                  </div>
                                  <div>
                                    <Label
                                      required
                                      id={`key`}
                                      name="Key"
                                      hint="Set up a unique key for value in your template. Format: text_text_text_.... Ex: is_refund"
                                    />
                                    <Input
                                      id={`key`}
                                      type="text"
                                      placeholder="Key"
                                      disabled={disabledKeyFieldsIds.includes(get(field, "field_id", ""))}
                                      error={get(
                                        errors,
                                        `fields.${index}.key.message`,
                                        ""
                                      )}
                                      {...register(`fields.${index}.key`)}
                                    />
                                    {get(
                                        errors,
                                        `fields.${index}.key`,
                                        ""
                                      ) ? (
                                      <p className="error-text">
                                        <span className="font-medium">
                                          {get(
                                            errors,
                                            `fields.${index}.key.message`,
                                            ""
                                          ) as string}
                                        </span>
                                      </p>
                                    ) : null}
                                  </div>
                                  <div className="grid grid-cols-1 items-center sm:grid-cols-3 gap-x-1 gap-y-4">
                                    <div>
                                      <Controller
                                        control={control}
                                        name={`fields.${index}.show`}
                                        render={({ field: { onChange, value = true, ref } }) => (
                                          <SwitchButton 
                                            label="Show"
                                            checked={value}
                                            onChange={(isChecked) => {
                                              onChange(isChecked);
                                              if (!isChecked) {
                                                setValue(`fields.${index}.require`, false);
                                              }
                                            }}
                                            disabled={disabledShowFieldsIds.includes(get(field, "field_id", ""))}
                                          />
                                        )}
                                      />
                                    </div>
                                    <div>
                                      <Controller
                                        control={control}
                                        name={`fields.${index}.require`}
                                        render={({ field: { onChange, value = true, ref } }) => (
                                          <SwitchButton 
                                            label="Required"
                                            checked={value}
                                            onChange={onChange}
                                            disabled={disabledRequireFieldsIds.includes(get(field, "field_id", ""))}
                                          />
                                        )}
                                      />
                                    </div>
                                    <div>
                                      <Controller
                                        control={control}
                                        name={`fields.${index}.is_lock_update_data`}
                                        render={({ field: { onChange, value = true, ref } }) => (
                                          <SwitchButton 
                                            label="Lock Edit"
                                            checked={value}
                                            onChange={onChange}
                                            disabled={disabledLockUpdateFieldsIds.includes(get(field, "field_id", ""))}
                                          />
                                        )}
                                      />
                                    </div>
                                  </div>
                                  {getValidationList(fieldsData[index]?.type).length > 0 ? (
                                    <div>
                                      <Label
                                        id={`validation`}
                                        name="Validation"
                                      />
                                      <Controller
                                        control={control}
                                        name={`fields.${index}.validation`}
                                        render={({ field: { onChange, value, ref } }) => {
                                          return (
                                            <Select
                                              key={`validation`}
                                              inputRef={ref}
                                              options={getValidationList(fieldsData[index]?.type)}
                                              id={`validation`}
                                              placeholder="Select Validation"
                                              isClearable={true}
                                              // defaultValue={getValidationList(fieldsData[index].type).find(
                                              //   (selected: { label: string; value: any }) =>
                                              //     selected.value === value
                                              // ) ?? ""}
                                              value={getValidationList(fieldsData[index]?.type).find(
                                                (selected: { label: string; value: any }) =>
                                                  selected.value === value
                                              ) ?? ""}
                                              onChange={(selected: { label: string; value: any }, {action} : any) => {
                                                if (action === "clear") {
                                                  onChange(ValidationType.NONE);
                                                  onChangeValidation(ValidationType.NONE, field.type, index);
                                                } else {
                                                  onChange(selected?.value);
                                                  onChangeValidation(selected.value, field.type, index);
                                                }
                                              }}
                                              error={get(
                                                errors,
                                                `fields.${index}.validation`,
                                                ""
                                              )}
                                            />
                                          );
                                        }}
                                      />
        
                                      {get(
                                          errors,
                                          `fields.${index}.validation`,
                                          ""
                                        ) ? (
                                        <p className="error-text">
                                          <span className="font-medium">
                                            {get(
                                              errors,
                                              `fields.${index}.validation.message`,
                                              ""
                                            ) as string}
                                          </span>
                                        </p>
                                      ) : null}
                                    </div>
                                  ) : <></>}
                                  {(fieldsData[index]?.validation === ValidationType.LENGTH || fieldsData[index]?.validation === ValidationType.DOB) ? (
                                    <div className="grid grid-cols-1 items-start sm:grid-cols-2 gap-x-4 gap-y-4 bg-magnolia-600 px-4 py-4 sm:px-6 -mx-4 sm:-mx-6 -mb-4 rounded-bl-lg rounded-br-lg">
                                      <div>
                                        <Label
                                          required
                                          id={`min`}
                                          name="Min"
                                        />
                                        <Input
                                          id={`min`}
                                          type="number"
                                          placeholder="Min"
                                          className="bg-white"
                                          min={1}
                                          error={get(
                                            errors,
                                            `fields.${index}.option.min.message`,
                                            ""
                                          )}
                                          {...register(`fields.${index}.option.min`, {
                                            valueAsNumber: true
                                          })}
                                        />
                                        {get(
                                            errors,
                                            `fields.${index}.option.min`,
                                            ""
                                          ) ? (
                                          <p className="error-text">
                                            <span className="font-medium">
                                              {get(
                                                errors,
                                                `fields.${index}.option.min.message`,
                                                ""
                                              ) as string}
                                            </span>
                                          </p>
                                        ) : null}
                                      </div>
                                      <div>
                                        <Label
                                          required
                                          id={`max`}
                                          name="Max"
                                        />
                                        <Input
                                          id={`max`}
                                          type="number"
                                          placeholder="Max"
                                          className="bg-white"
                                          min={1}
                                          error={get(
                                            errors,
                                            `fields.${index}.option.max.message`,
                                            ""
                                          )}
                                          {...register(`fields.${index}.option.max`, {
                                            valueAsNumber: true
                                          })}
                                        />
                                        {get(
                                            errors,
                                            `fields.${index}.option.max`,
                                            ""
                                          ) ? (
                                          <p className="error-text">
                                            <span className="font-medium">
                                              {get(
                                                errors,
                                                `fields.${index}.option.max.message`,
                                                ""
                                              ) as string}
                                            </span>
                                          </p>
                                        ) : null}
                                      </div>
                                      {fieldsData[index]?.validation === ValidationType.DOB ? (
                                        <div className="w-full col-span-full">
                                          <Controller
                                            control={control}
                                            name={`fields.${index}.option.by`}
                                            render={({ field: { onChange, value = "" } }) => {
                                              return (
                                                <div className="flex flex-col gap-y-2">
                                                  <div>
                                                    <Label
                                                      id={`fields.${index}.option.by`}
                                                      name="Calculate age as of a specific date"
                                                    />
                                                    <DatePicker
                                                      id={`fields.${index}.option.by`}
                                                      placeholder="Calculate age as of a specific date"
                                                      className="bg-white"
                                                      error={get(
                                                        errors,
                                                        `fields.${index}.option.by.message`,
                                                        ""
                                                      )}
                                                      value={value}
                                                      onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD"))}
                                                    />
                                                  </div>
                                                </div>
                                              );
                                            }}
                                          />
                                          {get(
                                            errors,
                                            `fields.${index}.option.by`,
                                            ""
                                          ) ? (
                                          <p className="error-text">
                                            <span className="font-medium">
                                              {get(
                                                errors,
                                                `fields.${index}.option.by.message`,
                                                ""
                                              ) as string}
                                            </span>
                                          </p>
                                        ) : null}
                                        </div>
                                      ) :<></>}
                                    </div>
                                  ) : <></>}
                                  {fieldsData[index]?.type === InputType.SELECT ? (
                                    <div>
                                      <Label
                                        required
                                        id={`object`}
                                        name="Object"
                                      />
                                      <Controller
                                        control={control}
                                        name={`fields.${index}.object`}
                                        render={({ field: { onChange, value, ref } }) => {
                                          return (
                                            <Select
                                              key={`object_list_${index}`}
                                              inputRef={ref}
                                              options={FIELD_OBJECT_LIST}
                                              id={`object`}
                                              placeholder="Select Object"
                                              value={FIELD_OBJECT_LIST.find(
                                                (selected: { label: string; value: any }) =>
                                                  selected.value === value
                                              ) ?? ""}
                                              onChange={(selected: { label: string; value: any }, {action} : any) => {
                                                if (action === "clear") {
                                                  onChange("");
                                                  onChangeObject("", index)
                                                } else {
                                                  onChange(selected.value);
                                                  onChangeObject(selected.value, index)
                                                }
                                              }}
                                              error={get(
                                                errors,
                                                `fields.${index}.validation`,
                                                ""
                                              )}
                                            />
                                          );
                                        }}
                                      />

                                      {get(
                                          errors,
                                          `fields.${index}.validation`,
                                          ""
                                        ) ? (
                                        <p className="error-text">
                                          <span className="font-medium">
                                            {get(
                                              errors,
                                              `fields.${index}.validation.message`,
                                              ""
                                            ) as string}
                                          </span>
                                        </p>
                                      ) : null}
                                    </div>
                                  ) : <></>}
                                  {isShowOptionForm(index) ? (
                                    <OptionForm templateIndex={templateIndex} groupIndex={groupIndex} fieldIndex={index} languages={languages} />
                                  ) : <></>}
                                </div>
                              </CollapsibleSection>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </DragDropContext>
          </div>
          <Button 
            className="w-full bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
            type="button"
            onClick={() => append(DEFAULT_NEW_FIELD)}
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" /> Add New Field
          </Button>
          <MoveFieldModal 
            languages={languages}
            open={moveFieldModalConfig.open}
            index={moveFieldModalConfig.index}
            onClose={_onCloseMoveFieldModal}
            groupIndex={groupIndex}
            groupFields={groupFields}
            onSubmit={(index, targetGroupIndex) => {
              if (groupIndex !== targetGroupIndex) {
                const movedField = fields[index];
                const updatedCurrentGroupFields = [...fields].filter((field, fieldIndex) => index !== fieldIndex);
                onMoveField(movedField, updatedCurrentGroupFields, groupIndex, targetGroupIndex);
              }
            }}
          />
        </div>
      </FormProvider>
    </RightSideModal>
  )
}